import React, { Component } from 'react';
import PropTypes from 'prop-types';

import PhoneAuthContainer from '../../../containers/auth/PhoneAuthContainer';
import EmailLoginRegisterContainer from '../../../containers/auth/EmailLoginRegisterContainer';
import EmailAuthLink from './EmailAuthLink';
import Button from '../../global/Button';
import ArrowRight from '../../icons/ArrowRight';
import { canRequestMagicLink } from '../../../util/product-permissions';
import { STEP as EMAIL_STEP } from '../EmailLoginRegister';
import BoxedArrowLink from '../../global/BoxedArrowLink';

const AUTH_MODES = {
  EMAIL_PASSWORD: 'EMAIL_PASSWORD',
  PHONE: 'PHONE',
  EMAIL_LINK: 'EMAIL_LINK',
};

class PhoneAuthWithEmailFallback extends Component {
  static propTypes = {
    onLogin: PropTypes.func,
    onRegister: PropTypes.func,
    // Fires both on login and on register
    onAuthSuccess: PropTypes.func,
    emailVerificationRedirectUrl: PropTypes.string,
    bypassEmailVerification: PropTypes.bool,
    enableEmail: PropTypes.bool,
    enableEmailFallback: PropTypes.bool,
    phoneAuthIntroText: PropTypes.string,
    defaultEmailStep: PropTypes.oneOf(Object.values(EMAIL_STEP)),
    introText: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    submitText: PropTypes.string,
    next: PropTypes.string,
  };

  static defaultProps = {
    defaultEmailStep: EMAIL_STEP.LOGIN,
  };

  state = {
    authMode: AUTH_MODES.PHONE,
  };

  setAuthMode = authMode => {
    this.setState(() => ({ authMode }));
  };

  render() {
    const { authMode } = this.state;
    const {
      onLogin,
      onRegister,
      onAuthSuccess,
      emailVerificationRedirectUrl,
      enableEmail,
      enableEmailFallback,
      bypassEmailVerification,
      phoneAuthIntroText,
      defaultEmailStep,
      introText,
      submitText,
      next,
    } = this.props;

    return (
      <div className="phone-auth-with-email-fallback">
        {/* Sign in with email address & password (if enabled). */}
        {authMode === AUTH_MODES.EMAIL_PASSWORD && (
          <EmailLoginRegisterContainer
            onLogin={onLogin}
            onRegister={onRegister}
            onAuthSuccess={onAuthSuccess}
            emailVerificationRedirectUrl={emailVerificationRedirectUrl}
            bypassEmailVerification={bypassEmailVerification}
            defaultStep={defaultEmailStep}
          >
            <BoxedArrowLink
              onClick={() => this.setAuthMode(AUTH_MODES.PHONE)}
              className="mt-3"
            >
              Login via phone
            </BoxedArrowLink>
          </EmailLoginRegisterContainer>
        )}

        {/**
         * Enter email, get sent auth link.
         *
         * Forward user to set password page after they email-link auth
         * If a secondaryRedirectRoute param is provided, specify it as
         * the next param for **after** they set their password. Eg. An
         * album onboarding (matches).
         *
         * Respect the bypassEmailVerification param, as a user logging
         * in will proceed differently to one signing up (eg no set
         * password step).
         */}
        {authMode === AUTH_MODES.EMAIL_LINK && (
          <EmailAuthLink
            backToPhoneAuth={() => this.setAuthMode(AUTH_MODES.PHONE)}
            next={
              emailVerificationRedirectUrl && !bypassEmailVerification
                ? `/auth/password-setup?next=${emailVerificationRedirectUrl}`
                : next
            }
          />
        )}

        {/* Default to Phone/Pin auth. */}
        {authMode === AUTH_MODES.PHONE && (
          <PhoneAuthContainer
            onLogin={onAuthSuccess || onLogin}
            phoneAuthIntroText={phoneAuthIntroText}
            introText={introText}
            submitText={submitText}
            onEmailAuth={() => this.setAuthMode(AUTH_MODES.EMAIL_PASSWORD)}
            enableEmailFallback={enableEmailFallback}
          >
            {enableEmail && (
              <BoxedArrowLink
                onClick={() => this.setAuthMode(AUTH_MODES.EMAIL_PASSWORD)}
                className="mt-2"
              >
                Login via email and password
              </BoxedArrowLink>
            )}

            {canRequestMagicLink() && (
              <Button
                onClick={() => this.setAuthMode(AUTH_MODES.EMAIL_LINK)}
                btnStyle="link"
                className="d-flex align-items-center mt-3"
              >
                Sign in via a link to your email{' '}
                <ArrowRight size="14" color="#0e84fe" className="ml-1" />
              </Button>
            )}
          </PhoneAuthContainer>
        )}
        <p className="mt-2 text-center">
          By continuing you agree to the{' '}
          <a
            className="d-block"
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Use
          </a>
        </p>
      </div>
    );
  }
}

export default PhoneAuthWithEmailFallback;
